import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import styled, { css, keyframes } from 'styled-components';

import { Target } from '../../../../client';
import ArrowButton from '../../../../components/ArrowButton';
import SwipePagination from '../../../../components/SwipePagination';
import WinaLoader from '../../../../components/WinaLoader';
import useDidUpdateEffect from '../../../../hooks/useDidUpdateEffect';
import { __ } from '../../../../i18n/l10n';
import theme from '../../../../styled-components/dark';
import { ZIndices } from '../../../../styled-components/dark/common';
import { GRRoundViewByOrder } from '../../constants/order';
import { GRHomeContext } from '../../contexts/GRHomeContext';
import { GRGlobalStatus, GRGlobalStatuses } from '../../types/global';
import { GRFullScreenNotification } from '../../types/notification';
import { GRRounds } from '../../types/round';
import GRRoundContainer from '../GRRound/GRRoundContainer';
import GRRoundCompleteNotificationTriggerer from '../GRRoundCompleteNotification/GRRoundCompleteNotificationContainer';
import GRRoundFailedNotificationTriggerer from '../GRRoundFailedNotification/GRRoundFailedNotificationContainer';
import GRSwipe from '../GRSwipe';
import GRViewWrapper from '../GRViewWrapper/GRViewWrapper';

const StyledGRViewWrapper = styled(GRViewWrapper)`
  padding: 0;
`;

type StyledWinaLoaderProps = {
  $isLoading: boolean;
};

const StyledWinaLoader = styled(WinaLoader)<StyledWinaLoaderProps>`
  ${({ $isLoading }) => css`
    position: absolute;
    top: 0;
    left: 0;
    margin: 0;
    width: 100%;
    height: 100%;
    opacity: ${$isLoading ? 1 : 0};
  `}
`;

const PaginationWrapper = styled.div`
  position: absolute;
  z-index: ${ZIndices.overlay + 1};
  top: 50%;
  left: 0;

  width: 100%;
  transform: translateY(-50%);
  display: flex;
  justify-content: space-between;
  align-items: center;
  pointer-events: none;

  font-size: 80%;
`;

type PaginationBtnSgaredStyleProps = {
  disabled: boolean;
};

const PaginationBtnSharedStyle = css<PaginationBtnSgaredStyleProps>`
  height: 3.5em;
  width: 2.5em;

  opacity: 0.7;
  border-radius: 0.3em;

  pointer-events: auto;

  ${({ disabled }) => {
    if (disabled) {
      return css`
        opacity: 0;
        pointer-events: none;
      `;
    }
  }}

  &:not(::disabled):hover {
    opacity: 1;
  }
`;

const PrevButton = styled(ArrowButton)`
  ${PaginationBtnSharedStyle}
  margin-left: 0.3em;
`;
const NextButton = styled(ArrowButton)`
  ${PaginationBtnSharedStyle}
  margin-right: 0.3em;
`;

const StyledGRRoundCompleteNotificationTriggerer = styled(GRRoundCompleteNotificationTriggerer)``;

const StyledGRRoundFailedNotificationTriggerer = styled(GRRoundFailedNotificationTriggerer)``;

const appearAnim = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const StyledSwipePagination = styled(SwipePagination)`
  position: absolute;
  z-index: 1;
  bottom: 1em;
  left: 50%;
  transform: translateX(-50%);

  &.gr-swipe-pagination-transition-enter {
    animation: ${appearAnim} 300ms ease-in-out forwards;
  }

  &.gr-swipe-pagination-transition-exit {
    animation: ${appearAnim} 300ms ease-in-out forwards reverse;
  }
`;

const OpeEndingTag = styled.div`
  position: absolute;
  top: 99%;
  left: 50%;
  width: 90%;
  transform: translate(-50%, -90%);
  pointer-events: none;
  opacity: 0.8;
  background: ${theme.KLHome.opeEndingTagBackgroundColor};
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.3em 0.5em;
  border-radius: 0.3em;
  font-weight: 600;
  font-size: 0.9em;
`;

type GRHomeProps = {
  index: number;
  setIndex: (index: number) => void;
  globalStatus: GRGlobalStatus;
  didUserWonLastRound: boolean;
  triggerNotificationsTransitions: () => void;
  cancelNotificationsTransitions: () => void;
  className?: string;
  endingLabel: string;
};

const GRHome = ({
  index,
  setIndex,
  globalStatus,
  triggerNotificationsTransitions,
  cancelNotificationsTransitions,
  didUserWonLastRound,
  className = '',
  endingLabel,
}: GRHomeProps) => {
  const [swipeIsLoaded, setSwipeIsLoaded] = useState(false);
  const [fullScreenNotifShown, setFullScreenNotifShown] = useState<GRFullScreenNotification | null>(null);
  const cancelNotificationsTransitionsRef = useRef<() => void>();

  const content = useMemo(
    () =>
      GRRoundViewByOrder.map((roundId) => {
        const displayWonEverything =
          globalStatus !== GRGlobalStatuses.ENDING && roundId === GRRounds.SHIRT && didUserWonLastRound;

        return (
          <GRRoundContainer key={`${roundId}-view`} roundId={roundId}>
            {globalStatus === GRGlobalStatuses.ENDING ? <OpeEndingTag>{endingLabel}</OpeEndingTag> : null}
            {displayWonEverything ? <OpeEndingTag>{__("You've already won everything!")}</OpeEndingTag> : null}
          </GRRoundContainer>
        );
      }),
    [globalStatus, didUserWonLastRound, endingLabel],
  );

  const onPrevClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();

      if (setIndex && index && index > 0) {
        setIndex(index - 1);
      }
    },
    [index, setIndex],
  );

  const onNextClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();

      if (setIndex && index < content?.length) {
        setIndex(index + 1);
      }
    },
    [content, index, setIndex],
  );

  const onPaginationChipClick = useCallback(
    (index: number) => {
      if (setIndex) {
        setIndex(index);
      }
    },
    [setIndex],
  );

  const isReady = useMemo(() => !!(index !== null && content?.length), [content, index]);
  const shouldDisplayPagination = useMemo(
    () => !Target.__MOBILE__ && swipeIsLoaded && isReady && !fullScreenNotifShown,
    [swipeIsLoaded, isReady, fullScreenNotifShown],
  );

  useEffect(() => {
    cancelNotificationsTransitionsRef.current = cancelNotificationsTransitions;
  }, [cancelNotificationsTransitions]);

  useEffect(() => {
    if (isReady) {
      triggerNotificationsTransitions();
    }
  }, [isReady, triggerNotificationsTransitions]);

  useDidUpdateEffect(() => {
    const fn = cancelNotificationsTransitionsRef.current;

    if (fn) {
      fn();
    }
  }, [index]);

  useEffect(() => {
    return () => {
      const fn = cancelNotificationsTransitionsRef.current;

      if (fn) {
        fn();
      }
    };
  }, []);

  const contextState = useMemo(
    () => ({
      fullScreenNotifShown,
      setFullScreenNotifShown,
    }),
    [fullScreenNotifShown, setFullScreenNotifShown],
  );

  return isReady ? (
    <GRHomeContext.Provider value={contextState}>
      <StyledGRViewWrapper className={className}>
        {shouldDisplayPagination ? (
          <PaginationWrapper>
            <PrevButton angle={180} onClick={onPrevClick} disabled={!index || index <= 0} />
            <NextButton angle={0} onClick={onNextClick} disabled={!!index && index >= content.length - 1} />
          </PaginationWrapper>
        ) : null}
        <StyledWinaLoader $isLoading={!swipeIsLoaded} />
        {/* @ts-ignore */}
        <GRSwipe
          setIndex={setIndex}
          indexToGo={index}
          disable={!Target.__MOBILE__ || Boolean(fullScreenNotifShown)}
          setIsLoaded={setSwipeIsLoaded}
          useKeyboard={false}
          fillSpace
          showNextPage
          noScale
          noPadding
          controlled
          fullHeight
          alwaysRenderEvery
        >
          {content}
        </GRSwipe>
        <CSSTransition
          classNames={'gr-swipe-pagination-transition'}
          in={!fullScreenNotifShown}
          timeout={300}
          unmountOnExit
          mountOnEnter
        >
          <StyledSwipePagination
            currIndex={index || 0}
            maxViews={GRRoundViewByOrder.length}
            onItemClick={onPaginationChipClick}
          />
        </CSSTransition>
        <StyledGRRoundCompleteNotificationTriggerer />
        <StyledGRRoundFailedNotificationTriggerer />
      </StyledGRViewWrapper>
    </GRHomeContext.Provider>
  ) : null;
};

export default GRHome;
